import axios from 'axios';
// Define network error types that we want to handle
export var NetworkErrorType;
(function (NetworkErrorType) {
    NetworkErrorType["INTERNET_DISCONNECTED"] = "INTERNET_DISCONNECTED";
    NetworkErrorType["SERVER_UNREACHABLE"] = "SERVER_UNREACHABLE";
    NetworkErrorType["SLOW_CONNECTION"] = "SLOW_CONNECTION";
    NetworkErrorType["SERVER_ERROR"] = "SERVER_ERROR";
    NetworkErrorType["TIMEOUT"] = "TIMEOUT";
    NetworkErrorType["UNKNOWN_ERROR"] = "UNKNOWN_ERROR"; // Other errors
})(NetworkErrorType || (NetworkErrorType = {}));
// Function to check internet connectivity using navigator.onLine
const checkInternetConnection = () => {
    return new Promise((resolve) => {
        // Use navigator.onLine for immediate connection status
        const isOnline = navigator.onLine;
        resolve(isOnline);
    });
};
/**
 * Safely extracts an error message from various response data formats
 */
const extractErrorMessage = (data) => {
    if (!data || typeof data !== 'object') {
        return undefined;
    }
    // Cast to any to avoid TypeScript errors while checking properties
    const errorData = data;
    // Check for common error message patterns
    if (typeof errorData.message === 'string') {
        return errorData.message;
    }
    if (typeof errorData.errorMessage === 'string') {
        return errorData.errorMessage;
    }
    if (typeof errorData.error === 'string') {
        return errorData.error;
    }
    if (errorData.error && typeof errorData.error === 'object' && typeof errorData.error.message === 'string') {
        return errorData.error.message;
    }
    if (typeof errorData.details === 'string') {
        return errorData.details;
    }
    return errorData;
};
// Helper function to determine error type
const determineNetworkErrorType = async (error) => {
    // Check if there's an internet connection first
    const isConnected = await checkInternetConnection();
    if (!isConnected) {
        return {
            type: NetworkErrorType.INTERNET_DISCONNECTED,
            message: 'No internet connection. Please check your network.',
            originalError: error,
        };
    }
    // Axios specific error handling
    if (axios.isAxiosError(error)) {
        const axiosError = error;
        // Check for timeout
        if (axiosError.code === 'ECONNABORTED') {
            return {
                type: NetworkErrorType.TIMEOUT,
                message: 'The request took too long to complete. Please try again.',
                originalError: error,
            };
        }
        // Client connectivity issues - internet problems detected via error message
        if (axiosError.message.includes('net::ERR_NETWORK_CHANGED') ||
            axiosError.message.includes('net::ERR_INTERNET_DISCONNECTED') ||
            axiosError.message.includes('net::ERR_NAME_NOT_RESOLVED')) {
            return {
                type: NetworkErrorType.INTERNET_DISCONNECTED,
                message: 'Internet connection issue detected. Please check your network.',
                originalError: error,
            };
        }
        // Server connectivity issues - server is likely down
        if (axiosError.message.includes('Network Error') ||
            axiosError.code === 'ERR_NETWORK' ||
            axiosError.message.includes('net::ERR_CONNECTION_REFUSED') ||
            axiosError.message.includes('net::ERR_EMPTY_RESPONSE')) {
            return {
                type: NetworkErrorType.SERVER_UNREACHABLE,
                message: 'Unable to connect to server. The service may be down or unreachable.',
                originalError: error,
            };
        }
        // Server errors (500 range)
        if (axiosError.response && axiosError.response.status >= 500) {
            return {
                type: NetworkErrorType.SERVER_ERROR,
                message: `Service Unavailable. Server error (${axiosError.response.status}).`,
                status: axiosError.response.status,
                originalError: error,
            };
        }
        // Client errors (400 range) - these are expected errors
        if (axiosError.response && axiosError.response.status >= 400 && axiosError.response.status < 500) {
            return {
                type: NetworkErrorType.SERVER_ERROR,
                message: extractErrorMessage(axiosError.response.data) ||
                    `Request failed with status code ${axiosError.response.status}`,
                status: axiosError.response.status,
                originalError: error,
            };
        }
    }
    // Default case - unknown error
    return {
        type: NetworkErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred. Please try again later.',
        originalError: error,
    };
};
// Create a function to create an axios instance with our interceptors
export const createApiInstance = (baseURL, timeout = 180000) => {
    // Create a custom axios instance with interceptors and timeout
    const apiInstance = axios.create({
        baseURL: baseURL || process.env.VITE_BASE_API_URL,
        timeout: timeout, // Default to 10 seconds timeout
    });
    // Set up request interceptor for adding headers, etc.
    apiInstance.interceptors.request.use(async (config) => {
        // Check internet connection before making requests
        const isConnected = await checkInternetConnection();
        if (!isConnected) {
            // Cancel the request if no internet connection
            return Promise.reject({
                type: NetworkErrorType.INTERNET_DISCONNECTED,
                message: 'No internet connection. Please check your network.',
            });
        }
        // Add authorization headers if needed
        // const token = localStorage.getItem('token');
        // if (token) {
        //   config.headers.Authorization = `Bearer ${token}`;
        // }
        return config;
    }, async (error) => {
        const networkError = await determineNetworkErrorType(error);
        return Promise.reject(networkError);
    });
    // Set up response interceptor for handling errors
    apiInstance.interceptors.response.use((response) => {
        return response;
    }, async (error) => {
        const networkError = await determineNetworkErrorType(error);
        // Log errors (you might want to send them to a monitoring service)
        console.error('API Request Failed:', networkError);
        // You can also dispatch actions to a global state manager (Redux, etc.)
        // store.dispatch(setNetworkError(networkError));
        return Promise.reject(networkError);
    });
    return apiInstance;
};
// Create the default API instance
const defaultApi = createApiInstance();
// Enhanced wrapper for api axios instance with optional custom baseURL
export const makeApiRequest = async (method, url, options) => {
    try {
        // Determine which API instance to use
        const apiInstance = options?.baseURL
            ? createApiInstance(options.baseURL)
            : defaultApi;
        const { data, config } = options || {};
        let response;
        switch (method.toLowerCase()) {
            case 'get':
                response = await apiInstance.get(url, config);
                break;
            case 'post':
                response = await apiInstance.post(url, data, config);
                break;
            case 'put':
                response = await apiInstance.put(url, data, config);
                break;
            case 'delete':
                const deleteConfig = {
                    ...config,
                    data: data
                };
                response = await apiInstance.delete(url, deleteConfig);
                break;
            default:
                throw new Error(`Unsupported method: ${method}`);
        }
        return {
            responseData: response.data,
            error: null,
        };
    }
    catch (error) {
        // The error should already be processed by our interceptor
        if (error.type && Object.values(NetworkErrorType).includes(error.type)) {
            return {
                responseData: null,
                error: error,
            };
        }
        // Fallback for unhandled errors
        const networkError = await determineNetworkErrorType(error);
        return {
            responseData: null,
            error: networkError,
        };
    }
};
// makeApiRequest with Typed Response and optional baseURL
export const makeApiRequestWithResponseType = async (method, url, options) => {
    const { data, params, config, errorMessage, baseURL } = options || {};
    // Merge params into config if provided
    const mergedConfig = {
        ...config,
        // If params are provided, add them to config
        ...(params && { params })
    };
    const result = await makeApiRequest(method, url, {
        data,
        config: mergedConfig,
        baseURL
    });
    return {
        responseData: result.responseData,
        error: result.error
            ? {
                // Use appropriate message based on error type
                errorMessage: 
                // For client-side connectivity issues, use the system message
                (result.error.type === NetworkErrorType.INTERNET_DISCONNECTED ||
                    result.error.type === NetworkErrorType.SLOW_CONNECTION) || result.error.type === NetworkErrorType.TIMEOUT
                    ? result.error.message
                    // For server unreachable, use a specific message
                    : result.error.type === NetworkErrorType.SERVER_UNREACHABLE
                        ? 'Unable to connect to server. Please try again later.'
                        // For other server errors, use the custom message or fall back to original
                        : (errorMessage || result.error.message),
                errorObjData: result.error,
            }
            : null,
    };
};
// Monitor connection status using the Navigator.onLine API and EventListeners
export const setupNetworkMonitoring = (onOffline, onOnline) => {
    // Initial check
    if (!navigator.onLine) {
        onOffline();
    }
    // Add event listeners for online/offline status
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // Return cleanup function
    return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
    };
};
